import type { AnyObject, FormApi } from 'final-form';
import setFieldData from 'final-form-set-field-data';
import React, { useContext, useEffect, useRef } from 'react';
import { Form } from 'react-final-form';

import type { PasswordRecoveryWizardData } from '@swf/auth';
import type { FieldsMap } from '@swf/components/StarsWizard/Machine';
import { getMeta } from '@swf/components/StarsWizard/Machine';
import useWizardContext from '@swf/core/hooks/useWizardContext';
import type { NameProp } from '@swf/core/types/Wizard';
import handleEnterPress from '@swf/core/utils/handleEnterPress';
import type { FirstWizardData, FirstWizardValues } from '@swf/first';

import { Context } from '../../StarsWizard';
import { Context as NavigationContext } from '../Navigation/Navigation';

interface Props extends NameProp {
  formClassName?: string;
}

const Page: React.FunctionComponent<React.PropsWithChildren<Props>> = ({
  formClassName,
  name,
  children
}) => {
  const context = useWizardContext<
    FirstWizardData & PasswordRecoveryWizardData,
    Partial<FirstWizardValues>
  >();
  const navigation = useContext(NavigationContext);
  const wizard = useContext(Context);
  const submitting = useRef(false);
  const waiting = useRef(false);

  useEffect(() => {
    if (!context || !wizard) {
      return undefined;
    }

    submitting.current = submitting.current && !context.error;

    const transitionHandler = ({ meta }: AnyObject) =>
      (waiting.current = (getMeta(meta) as Record<string, boolean>).waiting);

    wizard.onTransition(transitionHandler);

    return () => {
      wizard.off(transitionHandler);
    };
  }, [context, wizard]);

  const updateFieldsMap = (formApi: FormApi) => {
    if (wizard?.state.meta) {
      const newPageIdRef = `#${Object.keys(wizard.state.meta)}`;

      const existedIndex = context.data.fieldsMap.findIndex(
        ({ pageIdRef }: FieldsMap) => pageIdRef === newPageIdRef
      );
      if (existedIndex > -1) {
        context.data.fieldsMap = context.data.fieldsMap.slice(0, existedIndex);
      }

      context.data.fieldsMap.push({
        pageIdRef: newPageIdRef,
        fields: formApi.getRegisteredFields()
      });
    }
  };

  const goNext = (values: AnyObject, formApi: FormApi) => {
    if (!submitting.current && !waiting.current) {
      updateFieldsMap(formApi);
      submitting.current = true;
      navigation.goNext(values);
    }
  };

  return (
    // @ts-ignore //TODO: fix with final-form pending upgrade
    <Form onSubmit={goNext} initialValues={context.values} mutators={{ setFieldData }}>
      {({ handleSubmit }) => (
        <form
          id={name}
          onSubmit={handleSubmit}
          onKeyDown={handleEnterPress}
          noValidate
          className={formClassName}
        >
          {children}
        </form>
      )}
    </Form>
  );
};

export default Page;
